import { animate, state, style, transition, trigger } from '@angular/animations';
import { AfterViewInit, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { catchError, map, merge, of, startWith, switchMap } from 'rxjs';
import { User } from 'src/app/models/user';
import { RouteTrackingService } from 'src/app/services/route-tracking.service';
import { SubscriptionService } from 'src/app/services/subscription.service';
import { TrackParcelService } from 'src/app/services/track-parcel.service';
import { UserService } from 'src/app/services/user.service';
import { ModalService } from '../../services/modal.service';
import { ParcelUtilityService } from '../../services/parcel-utility.service';
import { SnackbarService } from '../../services/snackbar.service';

@Component({
	selector: 'app-parcels-server-side',
	templateUrl: './parcels-server-side.html',
	styleUrls: ['./parcels-server-side.scss'],
	animations: [
		trigger('detailExpand', [
			state('void', style({ height: '0px', minHeight: '0', visibility: 'hidden' })),
			state('*', style({ height: '*', visibility: 'visible' })),
			transition('void <=> *', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
		])
	]
})

export class ParcelsServerSideComponent implements OnInit, OnDestroy, AfterViewInit {
	public dataSource = new MatTableDataSource<any>();
	public displayedColumns: string[] = ['parcelId', 'barcode', 'masterBarcode', 'pieces', 'weight', 'parcelType', 'refrigerationType', 'action'];
	public page = 1;
	public parcels: any[] = [];
	public pageSizes = [25, 50, 100];
	public isLoading = false;
	public totalData: number;
	public parcelFilter = new UntypedFormControl('');
	@ViewChild(MatPaginator) public paginator: MatPaginator;
	@Input() public stopId: any;
	@Input() public areParcelsEditable: boolean;
	@Input() public custId: any;

	public user: User = new User();
	public subscriptions: any[] = [];

	constructor(private _routeTrackingService: RouteTrackingService,
				private _userService: UserService,
				private _subscriptionService: SubscriptionService,
				private _trackParcelService: TrackParcelService,
				private _parcelUtilityService: ParcelUtilityService,
				private _modalService: ModalService,
				private _snackbarService: SnackbarService) {
		_routeTrackingService.appParcelsServerSideRefresh$.subscribe(() => {
			this.refresh();
		});
	}

	ngOnInit(): void {
		this.subscriptions.push(this._userService.user$.subscribe((data) => {
			this.user = data;
		}));
	}

	ngOnDestroy(): void {
		this._subscriptionService.unSubscribe(this.subscriptions);
	}

	ngAfterViewInit(): void {
		this.dataSource.paginator = this.paginator;
		merge(this.parcelFilter.valueChanges, this.paginator.page)
			.pipe(
				startWith({}),
				switchMap(() => {
					this.isLoading = true;
					return this.getTableData$(
						this.paginator.pageIndex + 1,
						this.paginator.pageSize,
						this.parcelFilter.value
					).pipe(catchError(() => of(null)));
				}),
				map((result: any) => {
					if (result == null) return [];
					this.totalData = result.totalCount;
					this.isLoading = false;
					return result.parcels;
				})
			)
			.subscribe((parcels) => {
				this.parcels = parcels;
				this.dataSource = new MatTableDataSource(this.parcels);
			});
	}

	getTableData$(pageNum: Number, pageSize: Number, parcelFilter) {
		return this._routeTrackingService.getParcelsByStopId(this.stopId, parcelFilter, pageNum, pageSize)
	}

	filterParcels(value) {
		this.parcelFilter = value;
	}

	refresh() {
		this.isLoading = true;
		this.getTableData$(
			this.paginator.pageIndex + 1,
			this.paginator.pageSize,
			this.parcelFilter.value
		)
		.pipe(catchError(() => of(null)))
		.subscribe((result: any) => {
			if (result == null) {
				this.parcels = [];
			} else {
				this.totalData = result.totalCount;
				this.parcels = result.parcels;
			}
			this.dataSource = new MatTableDataSource(this.parcels);
			this.isLoading = false;
		});
	}

	stopPropagation(e): void {
		e.stopPropagation();
	}

	editParcel(parcel) {
		this._trackParcelService.editParcelModal(parcel, this.custId).then(() => {
			this._routeTrackingService.appParcelsServerSideRefresh$.emit();
			this._routeTrackingService.stopDetailRefresh$.emit(this.stopId);
			this._routeTrackingService.showMessage$.next(true);
		});
	}

	emailParcel(parcelId) {
		this._parcelUtilityService.emailParcel(parcelId);
	}

	printParcel(parcelId) {
		this._parcelUtilityService.printParcel(parcelId);
	}

	printParcelShippingLabels(parcelId) {
		this._modalService.displayPrintParcelShippingLabelModal(parcelId);
	}

	emailShippingLabels(parcelId) {
		const defaultSubject = `Shipping label for parcel ${parcelId}`;
		const defaultMessage = `${this.user.friendlyName || 'Your carrier'} has sent you a shipping label for parcel ${parcelId}. If you have any questions, please contact your carrier.`;
		const dialogRef = this._modalService.displayEmailParcelShippingLabelModal(parcelId, defaultSubject, defaultMessage);
		dialogRef.afterClosed().subscribe(result => {
			if (result) this._snackbarService.openSnackBar('Email Sent', 'Close', 5000, 'success');
		});
	}
}
